/**
 * Theme store — light / dark / system, persisted in localStorage.
 * Applied as data-theme attribute on <html> (see app.css).
 */

import { writable, get } from 'svelte/store'

export type Theme = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'theme'

function getInitialTheme(): Theme {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved
  }
  return 'system'
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  }
  return theme
}

function applyTheme(theme: Theme): void {
  const resolved = resolveTheme(theme)
  document.documentElement.dataset.theme = resolved
  document.documentElement.style.colorScheme = resolved
}

export const theme = writable<Theme>(getInitialTheme())

export function initTheme(): void {
  applyTheme(get(theme))
}

export function setTheme(next: Theme): void {
  theme.set(next)
  localStorage.setItem(STORAGE_KEY, next)
  applyTheme(next)
}

if (typeof window !== 'undefined') {
  // Follow OS preference changes while in "system" mode
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => applyTheme(get(theme)))
}
